import React, { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import Fuse from 'fuse.js';
import styles from './docs-spa.module.css';
import VirtualList from '../components/VirtualList';
import { useAuth } from '../contexts/AuthContext';
import { ModerationDashboard } from '../components/ModerationDashboard';
import { TableOfContents } from '../components/TableOfContents';
import { ScriptFileViewer } from '../components/ScriptFileViewer';
import { ReportIssueModal } from '../components/ReportIssueModal';
import { CommunityNoteIcon } from '../components/CommunityNoteIcon';
import { FloatingSearchButton } from '../components/FloatingSearchButton';
import { useTextSelectionSearch } from '../hooks/useTextSelectionSearch';
import { logger } from '../utils/logger';

// Lazy load visualization components for better performance
const InheritanceTree = lazy(() => import('../components/InheritanceTree'));
const ForceGraph = lazy(() => import('../components/ForceGraph'));
const SunburstDiagram = lazy(() => import('../components/SunburstDiagram'));

// Moderation Link Component
const ModerationLink: React.FC<{
  viewMode: 'docs' | 'moderation';
  setViewMode: (mode: 'docs' | 'moderation') => void;
}> = ({ viewMode, setViewMode }) => {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user?.is_moderator) {
    return null;
  }

  return (
    <button
      onClick={() => setViewMode(viewMode === 'docs' ? 'moderation' : 'docs')}
      style={{
        padding: '6px 12px',
        background: viewMode === 'moderation' ? '#007bff' : 'transparent',
        color: viewMode === 'moderation' ? 'white' : '#007bff',
        border: '1px solid #007bff',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '500',
        transition: 'all 0.2s'
      }}
    >
      {viewMode === 'moderation' ? 'Back to Docs' : 'Moderation'}
    </button>
  );
};

// Pattern category badge component
const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const categoryColors: { [key: string]: string } = {
    // Core categories (highest usage)
    accessors: '#9b59b6',         // Purple - 24.2%
    events: '#2ecc71',            // Green - 11.5%
    lifecycle: '#3498db',         // Blue - 8.6%
    inventory: '#16a085',         // Teal - 5.0%
    actions: '#e74c3c',           // Red - 3.9%
    syntax: '#e67e22',            // Orange - 3.6%
    static: '#95a5a6',            // Gray - 3.1%

    // UI & Display
    uidisplay: '#8e44ad',         // Purple - UI, menus, dialogs, tooltips

    // Visual & Audio
    visualeffects: '#e84393',     // Pink - Animations, particles
    audio: '#f39c12',             // Gold - Sound, audio playback

    // Validation & State
    validation: '#c0392b',        // Dark red - Validation, capability
    state: '#27ae60',             // Green - State management, toggles

    // Utility & Combat
    utility: '#bdc3c7',           // Light gray - General utilities
    combat: '#d63031',            // Crimson - Damage, weapons

    // Core Systems
    debug: '#7f8c8d',             // Gray - Debugging, editor
    updates: '#34495e',           // Dark blue-gray - Updates, refresh
    input: '#d35400',             // Dark orange - User input

    // Selection & Network
    selection: '#fd79a8',         // Rose - Focus, selection, cursors
    network: '#1abc9c',           // Turquoise - Network, RPC, sync
    system: '#16a085',            // Teal - System plugins, commands

    // Layout & Graphics
    layout: '#6c5ce7',            // Indigo - Layout, composition
    graphics: '#34495e',          // Dark blue - Camera, rotation

    // Gameplay & Player
    gameplay: '#00b894',          // Turquoise - Modifiers, physics
    player: '#fdcb6e',            // Yellow - Emotes, stamina

    // Data & IO
    data: '#2980b9',              // Blue - Context, categories, tags
    io: '#74b9ff',                // Light blue - File I/O, serialization

    // Progress & Timing
    progress: '#a29bfe',          // Lavender - Progress actions
    timing: '#0984e3',            // Sky blue - Timing, intervals

    // Specialized
    indicators: '#e67e22',        // Orange - Visual indicators
    component: '#9b59b6',         // Purple - Component management
    transform: '#27ae60',         // Green - Movement, transforms
    control: '#95a5a6',           // Gray - Control flow, processing

    // Lower usage
    filters: '#a29bfe',           // Lavender - Data filtering
    security: '#c0392b',          // Dark red - Locks, guards
    construction: '#16a085',      // Teal - Building, assembly

    other: '#95a5a6'              // Medium gray - Uncategorized
  };

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.125rem 0.5rem',
        fontSize: '0.75rem',
        fontWeight: '600',
        borderRadius: '4px',
        backgroundColor: categoryColors[category] || categoryColors.other,
        color: 'white',
        marginRight: '0.25rem',
        marginBottom: '0.25rem'
      }}
    >
      {category}
    </span>
  );
};

interface ClassData {
  n: string;  // name
  d: string;  // description
  f: string;  // file
  l: number;  // line
  e?: string; // extends
  a: string[]; // ancestors
  c: string;  // category
  p: PropertyData[];  // properties
  m: MethodData[];    // methods
  cfg?: { [key: string]: string[] };  // config properties
}

interface PropertyData {
  n: string;  // name
  t: string;  // type
  s: boolean; // static
  d: string;  // description
}

interface MethodData {
  n: string;   // name
  s: string;   // signature
  st: boolean; // static
  o: boolean;  // override
  d: string;   // description
  params: ParamData[];
  r: string;   // returns
  cat?: string[];  // pattern categories
}

interface ParamData {
  n: string;   // name
  t: string;   // type
  d: string;   // description
  dv?: string; // default value
}

interface DocsData {
  metadata: {
    generated: string;
    version?: string;
    total_classes: number;
    total_methods: number;
    total_properties: number;
    max_inheritance_depth: number;
  };
  classes: { [key: string]: ClassData };
  categories: { [key: string]: string[] };
  popular: string[];
}

interface ClassStats {
  total_methods: number;
  total_properties: number;
  override_count: number;
  override_ratio: number;
  static_count: number;
  static_ratio: number;
  avg_parameters: number;
  max_parameters: number;
  overloaded_methods: number;
  inheritance_depth: number;
  complexity_score: number;
  category_distribution: { [key: string]: number };
  top_category: string;
}

interface GlobalStats {
  total_classes: number;
  total_methods: number;
  total_properties: number;
  avg_methods_per_class: number;
  avg_properties_per_class: number;
  total_override_methods: number;
  global_override_ratio: number;
  total_static_methods: number;
  global_static_ratio: number;
  most_overridden_methods: { name: string; count: number }[];
  category_distribution: { [key: string]: number };
  max_inheritance_depth: number;
  avg_inheritance_depth: number;
  depth_distribution: { [key: string]: number };
  classes_with_config: number;
  total_config_properties: number;
  config_type_distribution: { [key: string]: number };
  avg_parameters_per_method: number;
  max_parameters_found: number;
  unique_method_names: number;
  method_name_reuse_ratio: number;
}

interface StatisticsData {
  metadata: {
    generated: string;
    version: string;
    source_file: string;
  };
  global: GlobalStats;
  classes: { [key: string]: ClassStats };
}

interface VersionInfo {
  build: string;
  scripts_rev: string;
  commit_hash: string;
  date: string;
  branch: string;
  file: string;
}

interface VersionsData {
  stable: VersionInfo[];
  experimental: VersionInfo[];
  latest_stable: string;
  latest_experimental: string;
}

interface DetailedClassDiff {
  className: string;
  oldClass: ClassData;
  newClass: ClassData;
  addedMethods: MethodData[];
  removedMethods: MethodData[];
  modifiedMethods: Array<{ old: MethodData; new: MethodData }>;
  unchangedMethods: MethodData[];
  addedProperties: PropertyData[];
  removedProperties: PropertyData[];
  modifiedProperties: Array<{ old: PropertyData; new: PropertyData }>;
  unchangedProperties: PropertyData[];
}

// Helper function to calculate detailed diff for a specific class
function calculateDetailedDiff(className: string, oldClass: ClassData, newClass: ClassData): DetailedClassDiff {
  const addedMethods: MethodData[] = [];
  const removedMethods: MethodData[] = [];
  const modifiedMethods: Array<{ old: MethodData; new: MethodData }> = [];
  const unchangedMethods: MethodData[] = [];

  const addedProperties: PropertyData[] = [];
  const removedProperties: PropertyData[] = [];
  const modifiedProperties: Array<{ old: PropertyData; new: PropertyData }> = [];
  const unchangedProperties: PropertyData[] = [];

  // Compare methods
  const oldMethodMap = new Map(oldClass.m.map(m => [m.n, m]));
  const newMethodMap = new Map(newClass.m.map(m => [m.n, m]));

  // Find added and modified methods
  for (const [name, newMethod] of newMethodMap) {
    const oldMethod = oldMethodMap.get(name);
    if (!oldMethod) {
      addedMethods.push(newMethod);
    } else {
      // Check if method changed (signature or params)
      if (oldMethod.s !== newMethod.s ||
          JSON.stringify(oldMethod.params) !== JSON.stringify(newMethod.params) ||
          oldMethod.r !== newMethod.r) {
        modifiedMethods.push({ old: oldMethod, new: newMethod });
      } else {
        unchangedMethods.push(newMethod);
      }
    }
  }

  // Find removed methods
  for (const [name, oldMethod] of oldMethodMap) {
    if (!newMethodMap.has(name)) {
      removedMethods.push(oldMethod);
    }
  }

  // Compare properties
  const oldPropMap = new Map(oldClass.p.map(p => [p.n, p]));
  const newPropMap = new Map(newClass.p.map(p => [p.n, p]));

  // Find added and modified properties
  for (const [name, newProp] of newPropMap) {
    const oldProp = oldPropMap.get(name);
    if (!oldProp) {
      addedProperties.push(newProp);
    } else {
      // Check if property changed (type or static)
      if (oldProp.t !== newProp.t || oldProp.s !== newProp.s) {
        modifiedProperties.push({ old: oldProp, new: newProp });
      } else {
        unchangedProperties.push(newProp);
      }
    }
  }

  // Find removed properties
  for (const [name, oldProp] of oldPropMap) {
    if (!newPropMap.has(name)) {
      removedProperties.push(oldProp);
    }
  }

  return {
    className,
    oldClass,
    newClass,
    addedMethods,
    removedMethods,
    modifiedMethods,
    unchangedMethods,
    addedProperties,
    removedProperties,
    modifiedProperties,
    unchangedProperties
  };
}

export default function DocsBrowser(): JSX.Element {
  const location = useLocation();
  const [data, setData] = useState<DocsData | null>(null);
  const [compareData, setCompareData] = useState<DocsData | null>(null);
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [versions, setVersions] = useState<VersionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedVersion, setSelectedVersion] = useState<string>('latest');
  const [selectedBranch, setSelectedBranch] = useState<string>('stable');
  const [compareMode, setCompareMode] = useState(false);
  const [compareVersion, setCompareVersion] = useState<string>('');
  const [compareBranch, setCompareBranch] = useState<string>('stable');
  const [fuse, setFuse] = useState<Fuse<any> | null>(null);
  const [methodUsageIndex, setMethodUsageIndex] = useState<Map<string, Set<string>>>(new Map());
  const [detailedDiff, setDetailedDiff] = useState<DetailedClassDiff | null>(null);
  const [classHistory, setClassHistory] = useState<string[]>([]);
  const [expandedAddedClass, setExpandedAddedClass] = useState<string | null>(null);
  const [expandedRemovedClass, setExpandedRemovedClass] = useState<string | null>(null);
  const [recentlyViewed, setRecentlyViewed] = useState<string[]>([]);
  const [visualizationType, setVisualizationType] = useState<'tree' | 'force' | 'sunburst' | null>(null);
  const [methodCategoryFilter, setMethodCategoryFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'docs' | 'moderation'>('docs');
  const [showReportIssueModal, setShowReportIssueModal] = useState(false);
  const [showScriptViewer, setShowScriptViewer] = useState(false);
  const [scriptViewerFile, setScriptViewerFile] = useState('');
  const [scriptViewerLine, setScriptViewerLine] = useState<number | undefined>(undefined);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const detailedDiffRef = useRef<HTMLDivElement>(null);

  // Version selector values are the unique per-version `file` id (or the
  // 'latest' sentinel), NOT the build number. DayZ build numbers are not
  // unique — the same build can appear twice in one branch (e.g. two distinct
  // 1.25.158593 stable commits) or across branches — so keying on build would
  // make one of the colliding versions unreachable and break its diff.
  const resolveVersion = (branch: string, id: string): VersionInfo | undefined => {
    if (!versions) return undefined;
    const list = branch === 'stable' ? versions.stable : versions.experimental;
    if (id === 'latest') return list[0];
    return list.find(v => v.file === id);
  };
  // Human-readable build number for a selector value, for display and for the
  // modals/props that still expect a build string.
  const buildLabel = (branch: string, id: string): string => {
    if (id === 'latest') {
      const latest = branch === 'stable' ? versions?.latest_stable : versions?.latest_experimental;
      return latest || 'latest';
    }
    return resolveVersion(branch, id)?.build ?? id;
  };

  // Text selection search functionality
  const selectionSearch = useTextSelectionSearch({
    usageIndex: methodUsageIndex,
    minLength: 3,
    debounceMs: 50
  });

  // Handle search from text selection
  const handleSelectionSearch = () => {
    const textToSearch = selectionSearch.text;
    if (textToSearch) {
      // Set the search term
      setSearchTerm(textToSearch);

      // Scroll to search section in sidebar after a small delay
      setTimeout(() => {
        const searchInput = document.querySelector('input[type="search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  // Debounce search term for performance (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Close mobile sidebar when a class is selected
  useEffect(() => {
    if (selectedClass && mobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  }, [selectedClass]);

  // Load recently viewed from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('dayz-docs-recent');
    if (stored) {
      try {
        setRecentlyViewed(JSON.parse(stored));
      } catch (e) {
        // Invalid data, ignore
      }
    }
  }, []);

  // Save recently viewed to localStorage whenever it changes
  useEffect(() => {
    if (recentlyViewed.length > 0) {
      localStorage.setItem('dayz-docs-recent', JSON.stringify(recentlyViewed));
    }
  }, [recentlyViewed]);

  // Scroll to detailed diff when it opens
  useEffect(() => {
    if (detailedDiff && detailedDiffRef.current) {
      detailedDiffRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [detailedDiff]);

  // Helper function to navigate to a class and track history
  const navigateToClass = useCallback((className: string) => {
    if (selectedClass) {
      // Add current class to history before navigating
      setClassHistory(prev => [...prev, selectedClass]);
    }
    setSelectedClass(className);

    // Add to recently viewed (keep max 12, remove duplicates, most recent first)
    setRecentlyViewed(prev => {
      const filtered = prev.filter(c => c !== className);
      return [className, ...filtered].slice(0, 12);
    });

    // Scroll to top - try multiple methods to ensure it works
    // Method 1: window.scrollTo
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Method 2: document.documentElement
    document.documentElement.scrollTop = 0;

    // Method 3: document.body
    document.body.scrollTop = 0;

    // Method 4: Find and scroll any main content containers
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
  }, [selectedClass]);

  // Helper function to go back in history
  const goBack = useCallback(() => {
    if (classHistory.length > 0) {
      const previousClass = classHistory[classHistory.length - 1];
      setClassHistory(prev => prev.slice(0, -1));
      setSelectedClass(previousClass);
    } else {
      // If no history, just close the class view
      setSelectedClass(null);
    }
  }, [classHistory]);

  // Load versions list on mount
  useEffect(() => {
    fetch('/data/versions.json')
      .then(res => res.json())
      .then((versionsData: VersionsData) => {
        setVersions(versionsData);
      })
      .catch(err => {
        logger.error('Failed to load versions data:', err);
      });
  }, []);

  // Load JSON data when version changes
  useEffect(() => {
    setLoading(true);

    // Determine which file to load
    let dataFile = '/data/classes.json'; // default/latest

    if (versions) {
      if (selectedVersion === 'latest') {
        // Load the latest version for the selected branch
        const versionList = selectedBranch === 'stable' ? versions.stable : versions.experimental;
        if (versionList.length > 0) {
          const latestVersion = versionList[0];
          dataFile = `/data/${latestVersion.file}`;
        }
      } else {
        // Find the specific version info
        const versionList = selectedBranch === 'stable' ? versions.stable : versions.experimental;
        const versionInfo = versionList.find(v => v.file === selectedVersion);

        if (versionInfo) {
          dataFile = `/data/${versionInfo.file}`;
        }
      }
    }

    // Determine statistics file
    const statsFile = dataFile.replace('classes', 'statistics');

    // Load both data and statistics in parallel
    Promise.all([
      fetch(dataFile).then(res => res.json()),
      fetch(statsFile).then(res => res.json()).catch(() => null) // Statistics optional
    ])
      .then(([jsonData, statsData]: [DocsData, StatisticsData | null]) => {
        setData(jsonData);
        setStatistics(statsData);

        // Initialize Fuse.js for fuzzy search
        const searchData = Object.entries(jsonData.classes).map(([name, classInfo]) => ({
          name,
          description: classInfo.d,
          category: classInfo.c,
          filePath: classInfo.f || '',
          methods: classInfo.m.map(m => m.n).join(' '),
          properties: classInfo.p.map(p => p.n).join(' ')
        }));

        const fuseInstance = new Fuse(searchData, {
          keys: [
            { name: 'name', weight: 2 },
            { name: 'filePath', weight: 1.5 },
            { name: 'description', weight: 1 },
            { name: 'methods', weight: 1.2 },
            { name: 'properties', weight: 0.8 }
          ],
          threshold: 0.35,
          includeScore: true,
          ignoreLocation: true,
          minMatchCharLength: 3,
          distance: 100
        });

        setFuse(fuseInstance);

        // Build method usage index for text selection search
        const usageIndex = new Map<string, Set<string>>();

        // Helper function to extract function/method calls from text
        const extractFunctionCalls = (text: string): string[] => {
          if (!text) return [];
          const calls: string[] = [];

          // Pattern 1: Direct function calls - FunctionName()
          const directCalls = text.matchAll(/\b([A-Z][a-zA-Z0-9_]*)\s*\(/g);
          for (const match of directCalls) {
            calls.push(match[1]);
          }

          // Pattern 2: Method calls - obj.MethodName()
          const methodCalls = text.matchAll(/\.([A-Z][a-zA-Z0-9_]*)\s*\(/g);
          for (const match of methodCalls) {
            calls.push(match[1]);
          }

          // Pattern 3: Static calls - Class::Method()
          const staticCalls = text.matchAll(/::([A-Z][a-zA-Z0-9_]*)\s*\(/g);
          for (const match of staticCalls) {
            calls.push(match[1]);
          }

          // Pattern 4: Chained method calls - GetGame().IsMultiplayer()
          // Captures the entire chain: "GetGame().IsMultiplayer()"
          const chainedCalls = text.matchAll(/([A-Z][a-zA-Z0-9_]*\(\)(?:\.[A-Z][a-zA-Z0-9_]*\(\))+)/g);
          for (const match of chainedCalls) {
            calls.push(match[1]);

            // Also index individual methods in the chain
            const methodsInChain = match[1].matchAll(/([A-Z][a-zA-Z0-9_]*)\(\)/g);
            for (const method of methodsInChain) {
              calls.push(method[1]);
            }
          }

          // Pattern 5: Static method calls with class - CfgGameplayHandler.GetUse3DMap()
          const staticMethodCalls = text.matchAll(/([A-Z][a-zA-Z0-9_]+)\.([A-Z][a-zA-Z0-9_]*)\s*\(/g);
          for (const match of staticMethodCalls) {
            // Index the full pattern: "ClassName.MethodName"
            calls.push(`${match[1]}.${match[2]}`);
            // Also index just the method name
            calls.push(match[2]);
            // And the class name
            calls.push(match[1]);
          }

          // Pattern 6: Static calls with :: - CfgGameplayHandler::GetUse3DMap()
          const colonStaticCalls = text.matchAll(/([A-Z][a-zA-Z0-9_]+)::([A-Z][a-zA-Z0-9_]*)\s*\(/g);
          for (const match of colonStaticCalls) {
            // Index the full pattern: "ClassName::MethodName"
            calls.push(`${match[1]}::${match[2]}`);
            // Also index just the method name
            calls.push(match[2]);
            // And the class name
            calls.push(match[1]);
          }

          // Pattern 7: Type casts - PlayerBase.Cast(entity)
          const casts = text.matchAll(/\b([A-Z][a-zA-Z0-9_]+)\.Cast\s*\(/g);
          for (const match of casts) {
            calls.push(match[1]);
          }

          // Pattern 8: Class references in type declarations - "PlayerBase player" or "ref PlayerBase"
          const typeRefs = text.matchAll(/\b(?:ref|out|inout|notnull|autoptr|array|map|set)\s+([A-Z][a-zA-Z0-9_]+)/gi);
          for (const match of typeRefs) {
            calls.push(match[1]);
          }

          // Pattern 9: Class instantiation - "new PlayerBase()"
          const instantiation = text.matchAll(/\bnew\s+([A-Z][a-zA-Z0-9_]+)\s*\(/g);
          for (const match of instantiation) {
            calls.push(match[1]);
          }

          // Pattern 10: Class names as standalone words (type declarations, extends, etc)
          // Match class names not already captured by other patterns
          const classNames = text.matchAll(/\b([A-Z][a-zA-Z0-9_]{2,})\b/g);
          for (const match of classNames) {
            // Only add if it looks like a class name (PascalCase with reasonable length)
            if (match[1].length >= 3) {
              calls.push(match[1]);
            }
          }

          return [...new Set(calls)]; // Deduplicate
        };

        // Index all classes, methods, properties, and function calls
        Object.entries(jsonData.classes).forEach(([className, classInfo]) => {
          // Index the class name itself (so "PlayerBase" search finds PlayerBase class)
          if (!usageIndex.has(className)) {
            usageIndex.set(className, new Set());
          }
          usageIndex.get(className)!.add(className);

          // Index parent class if it exists
          if (classInfo.parent) {
            if (!usageIndex.has(classInfo.parent)) {
              usageIndex.set(classInfo.parent, new Set());
            }
            usageIndex.get(classInfo.parent)!.add(className);
          }

          // Parse class description for references
          if (classInfo.desc) {
            const descriptionCalls = extractFunctionCalls(classInfo.desc);
            descriptionCalls.forEach(fnName => {
              if (!usageIndex.has(fnName)) {
                usageIndex.set(fnName, new Set());
              }
              usageIndex.get(fnName)!.add(className);
            });
          }

          // Index method names
          classInfo.m.forEach(method => {
            if (!usageIndex.has(method.n)) {
              usageIndex.set(method.n, new Set());
            }
            usageIndex.get(method.n)!.add(className);

            // Parse method signature and description for function calls
            const functionCalls = extractFunctionCalls(method.s + ' ' + method.d);
            functionCalls.forEach(fnName => {
              if (!usageIndex.has(fnName)) {
                usageIndex.set(fnName, new Set());
              }
              usageIndex.get(fnName)!.add(className);
            });
          });

          // Index property names
          classInfo.p.forEach(property => {
            if (!usageIndex.has(property.n)) {
              usageIndex.set(property.n, new Set());
            }
            usageIndex.get(property.n)!.add(className);

            // Parse property description for references
            if (property.d) {
              const propertyCalls = extractFunctionCalls(property.d);
              propertyCalls.forEach(fnName => {
                if (!usageIndex.has(fnName)) {
                  usageIndex.set(fnName, new Set());
                }
                usageIndex.get(fnName)!.add(className);
              });
            }
          });
        });

        setMethodUsageIndex(usageIndex);

        // Debug: Log index size and sample
        console.log(`[Text Search] Built usage index with ${usageIndex.size} unique terms`);
        console.log(`[Text Search] Total classes indexed: ${Object.keys(jsonData.classes).length}`);

        // Show a few example entries
        const sampleEntries = Array.from(usageIndex.entries()).slice(0, 5);
        console.log(`[Text Search] Sample entries:`, sampleEntries.map(([term, classes]) =>
          `${term} (${classes.size} classes)`
        ));

        // Debug: Check if PlayerBase is indexed
        if (usageIndex.has('PlayerBase')) {
          const playerBaseClasses = usageIndex.get('PlayerBase');
          console.log(`[Text Search] PlayerBase found in ${playerBaseClasses?.size} classes:`,
            Array.from(playerBaseClasses || []).slice(0, 10));
        } else {
          console.log(`[Text Search] PlayerBase NOT found in index`);
        }

        setLoading(false);
      })
      .catch(err => {
        logger.error('Failed to load documentation data:', err);
        setLoading(false);
      });
  }, [selectedVersion, selectedBranch, versions]);

  // Handle class parameter from URL (e.g., deep link to a specific class)
  useEffect(() => {
    if (!data) return; // Wait for data to load

    const params = new URLSearchParams(location.search);
    const classParam = params.get('class');

    if (classParam && data.classes[classParam]) {
      setSelectedClass(classParam);
    }
  }, [data, location.search]);

  // Load compare version data when compare mode is active
  useEffect(() => {
    if (!compareMode || !compareVersion || !versions) {
      setCompareData(null);
      return;
    }

    logger.log('Loading compare data:', { compareVersion, compareBranch });

    // Determine which file to load for comparison
    let dataFile = '/data/classes.json'; // default fallback

    if (compareVersion === 'latest') {
      // Load the latest version for the selected branch
      const versionList = compareBranch === 'stable' ? versions.stable : versions.experimental;
      if (versionList.length > 0) {
        const latestVersion = versionList[0];
        dataFile = `/data/${latestVersion.file}`;
        logger.log('Loading latest version file:', dataFile);
      }
    } else {
      // Find the specific version info
      const versionList = compareBranch === 'stable' ? versions.stable : versions.experimental;
      const versionInfo = versionList.find(v => v.file === compareVersion);

      if (versionInfo) {
        dataFile = `/data/${versionInfo.file}`;
        logger.log('Loading version file:', dataFile);
      }
    }

    logger.log('Fetching:', dataFile);
    fetch(dataFile)
      .then(res => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((jsonData: DocsData) => {
        logger.log('Compare data loaded successfully:', Object.keys(jsonData.classes).length, 'classes');
        setCompareData(jsonData);
      })
      .catch(err => {
        logger.error('Failed to load comparison data:', err);
        alert(`Failed to load comparison data: ${err.message}`);
      });
  }, [compareMode, compareVersion, compareBranch, versions]);

  // Calculate version diff when in compare mode
  const versionDiff = useMemo(() => {
    if (!compareMode || !data || !compareData) return null;

    const addedClasses: string[] = [];
    const removedClasses: string[] = [];
    const modifiedClasses: string[] = [];
    const unchangedClasses: string[] = [];

    const oldClasses = new Set(Object.keys(compareData.classes));
    const newClasses = new Set(Object.keys(data.classes));

    // Find added and modified classes
    for (const className of newClasses) {
      if (!oldClasses.has(className)) {
        addedClasses.push(className);
      } else {
        // Check if modified (compare method/property counts as quick check)
        const oldClass = compareData.classes[className];
        const newClass = data.classes[className];
        if (oldClass.m.length !== newClass.m.length ||
            oldClass.p.length !== newClass.p.length) {
          modifiedClasses.push(className);
        } else {
          unchangedClasses.push(className);
        }
      }
    }

    // Find removed classes
    for (const className of oldClasses) {
      if (!newClasses.has(className)) {
        removedClasses.push(className);
      }
    }

    return {
      added: addedClasses.sort(),
      removed: removedClasses.sort(),
      modified: modifiedClasses.sort(),
      unchanged: unchangedClasses.sort(),
      summary: {
        added: addedClasses.length,
        removed: removedClasses.length,
        modified: modifiedClasses.length,
        unchanged: unchangedClasses.length
      }
    };
  }, [compareMode, data, compareData]);

  // Compute inline class diff when viewing a specific class in compare mode
  const inlineClassDiff = useMemo(() => {
    if (!compareMode || !selectedClass || !data || !compareData) return null;

    const newClass = data.classes[selectedClass];
    const oldClass = compareData.classes[selectedClass];

    // Class doesn't exist in one of the versions
    if (!newClass && !oldClass) return null;
    if (!newClass) return { status: 'removed', oldClass, newClass: null };
    if (!oldClass) return { status: 'added', oldClass: null, newClass };

    // Compare properties
    const oldProps = new Map(oldClass.p.map((p: any) => [p.n, p]));
    const newProps = new Map(newClass.p.map((p: any) => [p.n, p]));

    const addedProps: any[] = [];
    const removedProps: any[] = [];
    const modifiedProps: any[] = [];
    const unchangedProps: any[] = [];

    newClass.p.forEach((prop: any) => {
      if (!oldProps.has(prop.n)) {
        addedProps.push(prop);
      } else {
        const oldProp = oldProps.get(prop.n);
        if (JSON.stringify(oldProp) !== JSON.stringify(prop)) {
          modifiedProps.push({ old: oldProp, new: prop });
        } else {
          unchangedProps.push(prop);
        }
      }
    });

    oldClass.p.forEach((prop: any) => {
      if (!newProps.has(prop.n)) {
        removedProps.push(prop);
      }
    });

    // Compare methods
    // For overloaded methods, we need to match them by position and name, then check for changes
    const addedMethods: any[] = [];
    const removedMethods: any[] = [];
    const modifiedMethods: any[] = [];
    const unchangedMethods: any[] = [];

    // Group methods by name to handle overloading
    const groupByName = (methods: any[]) => {
      const groups: { [key: string]: any[] } = {};
      methods.forEach((m: any) => {
        if (!groups[m.n]) groups[m.n] = [];
        groups[m.n].push(m);
      });
      return groups;
    };

    const oldMethodGroups = groupByName(oldClass.m);
    const newMethodGroups = groupByName(newClass.m);

    // Get all unique method names
    const allMethodNames = new Set([...Object.keys(oldMethodGroups), ...Object.keys(newMethodGroups)]);

    allMethodNames.forEach(methodName => {
      const oldOverloads = oldMethodGroups[methodName] || [];
      const newOverloads = newMethodGroups[methodName] || [];

      // Match overloads by position (same index = same overload)
      const maxLength = Math.max(oldOverloads.length, newOverloads.length);

      for (let i = 0; i < maxLength; i++) {
        const oldMethod = oldOverloads[i];
        const newMethod = newOverloads[i];

        if (!oldMethod && newMethod) {
          // New overload added
          addedMethods.push(newMethod);
        } else if (oldMethod && !newMethod) {
          // Overload removed
          removedMethods.push(oldMethod);
        } else if (oldMethod && newMethod) {
          // Compare the two overloads
          const oldStr = JSON.stringify(oldMethod);
          const newStr = JSON.stringify(newMethod);
          if (oldStr !== newStr) {
            modifiedMethods.push({ old: oldMethod, new: newMethod });
          } else {
            unchangedMethods.push(newMethod);
          }
        }
      }
    });

    const hasChanges = addedProps.length > 0 || removedProps.length > 0 || modifiedProps.length > 0 ||
                      addedMethods.length > 0 || removedMethods.length > 0 || modifiedMethods.length > 0;

    return {
      status: hasChanges ? 'modified' : 'unchanged',
      oldClass,
      newClass,
      properties: {
        added: addedProps,
        removed: removedProps,
        modified: modifiedProps,
        unchanged: unchangedProps
      },
      methods: {
        added: addedMethods,
        removed: removedMethods,
        modified: modifiedMethods,
        unchanged: unchangedMethods
      }
    };
  }, [compareMode, selectedClass, data, compareData]);

  // State to track last modified version for selected class
  const [lastModifiedInfo, setLastModifiedInfo] = useState<{ version: string; build: string } | null>(null);

  // Effect to find when the selected class was last modified
  useEffect(() => {
    if (!selectedClass || !versions || !data) {
      setLastModifiedInfo(null);
      return;
    }

    const versionList = selectedBranch === 'stable' ? versions.stable : versions.experimental;
    if (versionList.length === 0) return;

    const currentClass = data.classes[selectedClass];
    if (!currentClass) return;

    // Check versions from newest to oldest to find when this class was last modified
    const checkVersions = async () => {
      let lastModified = null;

      // Start from the current version index
      let currentIndex = selectedVersion === 'latest' ? 0 : versionList.findIndex(v => v.file === selectedVersion);
      if (currentIndex === -1) currentIndex = 0;

      // Work backwards through versions to find the last modification
      try {
        let previousClass = currentClass;

        for (let i = currentIndex + 1; i < versionList.length; i++) {
          const response = await fetch(`/data/${versionList[i].file}`);
          const olderData = await response.json();
          const olderClass = olderData.classes[selectedClass];

          if (!olderClass) {
            // Class didn't exist in this older version, so it was added in the previous checked version
            lastModified = { version: versionList[i - 1].file, build: versionList[i - 1].build };
            break;
          }

          // Compare current iteration's class with the older one
          const hasChanges =
            JSON.stringify(previousClass.m) !== JSON.stringify(olderClass.m) ||
            JSON.stringify(previousClass.p) !== JSON.stringify(olderClass.p) ||
            previousClass.e !== olderClass.e;

          if (hasChanges) {
            // Found where it changed - it was modified in the previous version we checked
            lastModified = { version: versionList[i - 1].file, build: versionList[i - 1].build };
            break;
          }

          // No changes yet, keep going back
          previousClass = olderClass;
        }

        // If we got through all versions without finding changes, it was added in the oldest version
        if (!lastModified && versionList.length > 1) {
          lastModified = { version: versionList[versionList.length - 1].file, build: versionList[versionList.length - 1].build };
        }
      } catch (error) {
        logger.error('Failed to load version for comparison:', error);
      }

      setLastModifiedInfo(lastModified);
    };

    checkVersions();
  }, [selectedClass, versions, selectedBranch, selectedVersion, data]);

  // Search results (using debounced search term for performance)
  // Virtual scrolling enabled - can handle 1000+ items efficiently
  const searchResults = useMemo(() => {
    if (!data || !fuse) return [];

    if (!debouncedSearchTerm.trim()) {
      // No search term - show popular classes or all classes
      let classList = data.popular;
      if (selectedCategory !== 'all') {
        classList = data.categories[selectedCategory] || [];
      }
      return classList;
    }

    const searchLower = debouncedSearchTerm.toLowerCase();
    const exactMatches: string[] = [];
    const substringMatches: string[] = [];
    const methodMatches: string[] = [];
    const inheritanceMatches: string[] = [];
    const fuzzyMatches: string[] = [];

    // Helper function to find all child classes of a given parent
    const findChildClasses = (parentName: string): string[] => {
      const children: string[] = [];
      Object.entries(data.classes).forEach(([name, classInfo]) => {
        if (classInfo.e === parentName) {
          children.push(name);
          // Recursively find children of children
          children.push(...findChildClasses(name));
        }
      });
      return children;
    };

    // First pass: exact and substring matches
    Object.entries(data.classes).forEach(([name, classInfo]) => {
      const nameLower = name.toLowerCase();
      const filePath = (classInfo.f || '').toLowerCase();

      // Method/property match (HIGHEST PRIORITY for specific searches)
      const hasMethod = classInfo.m.some(m => m.n.toLowerCase().includes(searchLower));
      const hasProperty = classInfo.p.some(p => p.n.toLowerCase().includes(searchLower));

      if (hasMethod || hasProperty) {
        methodMatches.push(name);
        return; // Don't continue checking - method match is most specific
      }

      // Exact class name match
      if (nameLower === searchLower) {
        exactMatches.push(name);
        return;
      }

      // Substring match in class name (only if search term is significant length)
      if (searchLower.length >= 4 && nameLower.includes(searchLower)) {
        substringMatches.push(name);
        return;
      }

      // File path match
      if (filePath.includes(searchLower)) {
        substringMatches.push(name);
      }
    });

    // Add inheritance chain matches
    // For each exact or substring match, find all child classes
    const matchedClasses = [...exactMatches, ...substringMatches];
    const alreadyMatched = new Set(matchedClasses);

    matchedClasses.forEach(className => {
      const children = findChildClasses(className);
      children.forEach(child => {
        if (!alreadyMatched.has(child)) {
          inheritanceMatches.push(child);
          alreadyMatched.add(child);
        }
      });
    });

    // Second pass: fuzzy search for remaining items (only if we have few exact matches)
    if (exactMatches.length + substringMatches.length + methodMatches.length < 5) {
      const results = fuse.search(debouncedSearchTerm);
      const alreadyFound = new Set([...exactMatches, ...substringMatches, ...methodMatches, ...inheritanceMatches]);

      results.forEach(r => {
        if (!alreadyFound.has(r.item.name) && fuzzyMatches.length < 5) {
          fuzzyMatches.push(r.item.name);
        }
      });
    }

    // Combine results in priority order (methods first for specific searches)
    let filtered = [...methodMatches, ...exactMatches, ...substringMatches, ...inheritanceMatches, ...fuzzyMatches];

    // Filter by category
    if (selectedCategory !== 'all') {
      const categoryClasses = new Set(data.categories[selectedCategory] || []);
      filtered = filtered.filter(name => categoryClasses.has(name));
    }

    return filtered;
  }, [data, fuse, debouncedSearchTerm, selectedCategory]);

  // Track which results are from inheritance chains for visual indicators
  const inheritanceSet = useMemo(() => {
    if (!data || !debouncedSearchTerm.trim()) return new Set<string>();

    const searchLower = debouncedSearchTerm.toLowerCase();
    const matchedClasses: string[] = [];
    const inheritanceMatches = new Set<string>();

    // Find direct matches
    Object.entries(data.classes).forEach(([name, classInfo]) => {
      const nameLower = name.toLowerCase();
      if (nameLower === searchLower || (searchLower.length >= 4 && nameLower.includes(searchLower))) {
        matchedClasses.push(name);
      }
    });

    // Helper function to find all child classes
    const findChildClasses = (parentName: string): string[] => {
      const children: string[] = [];
      Object.entries(data.classes).forEach(([name, classInfo]) => {
        if (classInfo.e === parentName) {
          children.push(name);
          children.push(...findChildClasses(name));
        }
      });
      return children;
    };

    // Find all children of matched classes
    matchedClasses.forEach(className => {
      const children = findChildClasses(className);
      children.forEach(child => inheritanceMatches.add(child));
    });

    return inheritanceMatches;
  }, [data, debouncedSearchTerm]);

  // Selected class data - check both new version and compare version (for removed classes)
  const currentClass = selectedClass && data
    ? (data.classes[selectedClass] || (compareData ? compareData.classes[selectedClass] : null))
    : null;

  // Table of Contents sections
  const tocSections = useMemo(() => {
    if (!currentClass) return [];

    const sections = [
      { id: 'overview', label: 'Overview', level: 1 },
      { id: 'inheritance-visualization', label: 'Inheritance Visualization', level: 1 },
    ];

    // Add config properties if they exist
    if (currentClass.cfg && Object.keys(currentClass.cfg).length > 0) {
      sections.push({ id: 'config-properties', label: 'Config Properties', level: 1 });
    }

    // Add class statistics if available
    if (statistics && statistics.classes[currentClass.n]) {
      sections.push({ id: 'class-statistics', label: 'Class Statistics', level: 1 });
    }

    // Add properties if they exist
    if (currentClass.p.length > 0) {
      sections.push({ id: 'properties', label: `Properties (${currentClass.p.length})`, level: 1 });
    }

    // Add methods if they exist
    if (currentClass.m.length > 0) {
      sections.push({ id: 'methods', label: `Methods (${currentClass.m.length})`, level: 1 });
    }

    return sections;
  }, [currentClass, statistics]);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading documentation data...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Failed to load documentation data</div>
      </div>
    );
  }

  return (
    <>
      {/* Top Version Bar */}
      {versions && (
          <div className={styles.topVersionBar}>
            <div className={styles.topVersionBarContent}>
              {/* Left Side - Title and Stats */}
              <div className={styles.titleSection}>
                <h2 className={styles.topBarTitle}>DayZ Script Documentation</h2>
                <p className={styles.topBarStats}>
                  {data.metadata.total_classes.toLocaleString()} classes •{' '}
                  {data.metadata.total_methods.toLocaleString()} methods •{' '}
                  {data.metadata.total_properties.toLocaleString()} properties
                </p>
              </div>

              {/* Right Side - Version Controls and Compare */}
              <div className={styles.versionControls}>
                {/* Primary Version Selector */}
                <div className={styles.primaryVersionSelector}>
                  <label className={styles.versionLabel}>Branch:</label>
                  <select
                    className={styles.versionDropdown}
                    value={selectedBranch}
                    onChange={(e) => {
                      setSelectedBranch(e.target.value);
                      setSelectedVersion('latest');
                    }}
                  >
                    <option value="stable">Stable</option>
                    <option value="experimental">Experimental</option>
                  </select>

                  <label className={styles.versionLabel}>Version:</label>
                  <select
                    className={styles.versionDropdown}
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                  >
                    <option value="latest">Latest ({selectedBranch === 'stable' ? versions.latest_stable : versions.latest_experimental})</option>
                    {(selectedBranch === 'stable' ? versions.stable : versions.experimental).map(v => (
                      <option key={v.file} value={v.file}>
                        {v.build} ({v.date})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Compare Controls (anchored right) */}
                <div className={styles.compareControls}>
                  {/* Compare Version Selector (appears before checkbox when enabled) */}
                  {compareMode && (
                    <div className={styles.compareVersionSelector}>
                      <label className={styles.versionLabel}>vs</label>
                      <select
                        className={styles.versionDropdown}
                        value={compareBranch}
                        onChange={(e) => {
                          setCompareBranch(e.target.value);
                          setCompareVersion('');
                        }}
                      >
                        <option value="stable">Stable</option>
                        <option value="experimental">Experimental</option>
                      </select>

                      <select
                        className={styles.versionDropdown}
                        value={compareVersion}
                        onChange={(e) => setCompareVersion(e.target.value)}
                      >
                        <option value="">Select version...</option>
                        <option value="latest">Latest ({compareBranch === 'stable' ? versions.latest_stable : versions.latest_experimental})</option>
                        {(compareBranch === 'stable' ? versions.stable : versions.experimental).map(v => (
                          <option key={v.file} value={v.file}>
                            {v.build} ({v.date})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Compare Mode Toggle (always at far right) */}
                  <div className={styles.compareToggleContainer}>
                    <label className={styles.compareToggleLabel}>
                      <input
                        type="checkbox"
                        checked={compareMode}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setCompareMode(isChecked);
                          if (isChecked) {
                            // Set default compare version to first available (not latest)
                            const versionList = compareBranch === 'stable' ? versions.stable : versions.experimental;
                            if (versionList.length > 0) {
                              // Use the first non-latest version if available
                              setCompareVersion(versionList[0].file);
                            }
                            // Keep selected class to show inline comparison if viewing a class
                          } else {
                            // Reset compare data when turning off compare mode
                            setCompareData(null);
                            setCompareVersion('');
                          }
                        }}
                      />
                      <span>Compare</span>
                    </label>
                  </div>
                </div>
              </div>
          </div>
        </div>
      )}

      {/* Moderation Dashboard View */}
      {viewMode === 'moderation' ? (
        <div style={{ padding: '20px' }}>
          <ModerationDashboard />
        </div>
      ) : (
        <div className={styles.container}>
          {/* Mobile Sidebar Overlay */}
          <div
            className={`${styles.sidebarOverlay} ${mobileSidebarOpen ? styles.open : ''}`}
            onClick={() => setMobileSidebarOpen(false)}
          />

          {/* Sidebar - Search and Results */}
          <aside className={`${styles.sidebar} ${mobileSidebarOpen ? styles.open : ''}`}>
          <div className={styles.searchSection}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search classes, file paths, methods, properties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />

            <select
              className={styles.categoryFilter}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {Object.keys(data.categories).sort().map(cat => (
                <option key={cat} value={cat}>
                  {cat} ({data.categories[cat].length})
                </option>
              ))}
            </select>
          </div>

          {searchResults.length === 0 ? (
            <div className={styles.resultsList}>
              <div className={styles.noResults}>No classes found</div>
            </div>
          ) : (
            <VirtualList
              items={searchResults}
              height={600}
              itemHeight={100}
              className={styles.resultsList}
              renderItem={(className) => {
                const classInfo = data.classes[className];
                const isInheritanceMatch = inheritanceSet.has(className);
                return (
                  <div
                    className={`${styles.resultItem} ${selectedClass === className ? styles.selected : ''}`}
                    onClick={() => navigateToClass(className)}
                  >
                    <div className={styles.className}>
                      {className}
                      {isInheritanceMatch && (
                        <span style={{
                          marginLeft: '0.5rem',
                          fontSize: '0.75rem',
                          padding: '0.125rem 0.5rem',
                          background: 'var(--ifm-color-primary-lightest)',
                          color: 'var(--ifm-color-primary-dark)',
                          borderRadius: '4px',
                          fontWeight: '600'
                        }} title="Found via inheritance chain">
                          🔗 Child Class
                        </span>
                      )}
                    </div>
                    <div className={styles.classCategory}>{classInfo.c}</div>
                    {classInfo.f && (
                      <div className={styles.classFilePath}>📁 {classInfo.f}</div>
                    )}
                    {classInfo.e && (
                      <div className={styles.classExtends}>extends {classInfo.e}</div>
                    )}
                  </div>
                );
              }}
            />
          )}
        </aside>

        {/* Main Content - Class Details or Comparison View */}
        <main className={styles.mainContent}>
          {compareMode && compareData && versionDiff && !selectedClass ? (
            <div className={styles.diffView}>
              <div className={styles.diffSummary}>
                <h2>Version Comparison</h2>
                <p>
                  <strong>{selectedVersion === 'latest' ? `Latest ${selectedBranch}` : buildLabel(selectedBranch, selectedVersion)}</strong>
                  {' '} vs <strong>{compareVersion === 'latest' ? `Latest ${compareBranch}` : buildLabel(compareBranch, compareVersion)}</strong>
                </p>

                <div className={styles.diffStats}>
                  <div
                    className={`${styles.diffStat} ${styles.added}`}
                    onClick={() => {
                      const element = document.getElementById('added-section');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={styles.statLabel}>Added</span>
                    <span className={styles.statValue}>{versionDiff.summary.added}</span>
                  </div>
                  <div
                    className={`${styles.diffStat} ${styles.removed}`}
                    onClick={() => {
                      const element = document.getElementById('removed-section');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={styles.statLabel}>Removed</span>
                    <span className={styles.statValue}>{versionDiff.summary.removed}</span>
                  </div>
                  <div
                    className={`${styles.diffStat} ${styles.modified}`}
                    onClick={() => {
                      const element = document.getElementById('modified-section');
                      element?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={styles.statLabel}>Modified</span>
                    <span className={styles.statValue}>{versionDiff.summary.modified}</span>
                  </div>
                  <div
                    className={`${styles.diffStat} ${styles.unchanged}`}
                    style={{ cursor: 'default', opacity: 0.7 }}
                  >
                    <span className={styles.statLabel}>Unchanged</span>
                    <span className={styles.statValue}>{versionDiff.summary.unchanged}</span>
                  </div>
                </div>
              </div>

              {versionDiff.added.length > 0 && (
                <section id="added-section" className={`${styles.diffSection} ${styles.added}`}>
                  <h3>Added Classes ({versionDiff.added.length})</h3>
                  <div className={styles.modifiedClassesContainer}>
                    {versionDiff.added.map(className => {
                      const isExpanded = expandedAddedClass === className;
                      const classData = data.classes[className];

                      return (
                        <React.Fragment key={className}>
                          <div
                            className={`${styles.diffClassItem} ${styles.added} ${isExpanded ? styles.expanded : ''}`}
                            onClick={(e) => {
                              // Check if clicking the "View Full Documentation" button
                              if ((e.target as HTMLElement).closest('button')) {
                                return; // Let the button handle its own click
                              }
                              if (isExpanded) {
                                setExpandedAddedClass(null);
                              } else {
                                setExpandedAddedClass(className);
                              }
                            }}
                          >
                            {className} {isExpanded ? '▼' : '▶'}
                          </div>

                          {isExpanded && classData && (
                            <div className={styles.detailedDiffInline}>
                              <div className={styles.classQuickView}>
                                <h3 className={styles.detailedDiffTitle}>
                                  {className}
                                  <button
                                    className={styles.viewFullButton}
                                    onClick={() => {
                                      setClassHistory([]);
                                      setSelectedClass(className);
                                    }}
                                  >
                                    View Full Documentation →
                                  </button>
                                </h3>

                                {classData.d && (
                                  <p className={styles.classDescription}>{classData.d}</p>
                                )}

                                <div className={styles.classMetadata}>
                                  {classData.e && <div><strong>Extends:</strong> {classData.e}</div>}
                                  <div><strong>Category:</strong> {classData.c}</div>
                                  <div><strong>Properties:</strong> {classData.p.length}</div>
                                  <div><strong>Methods:</strong> {classData.m.length}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </section>
              )}

              {versionDiff.removed.length > 0 && (
                <section id="removed-section" className={`${styles.diffSection} ${styles.removed}`}>
                  <h3>Removed Classes ({versionDiff.removed.length})</h3>
                  <div className={styles.modifiedClassesContainer}>
                    {versionDiff.removed.map(className => {
                      const isExpanded = expandedRemovedClass === className;
                      const classData = compareData.classes[className];

                      return (
                        <React.Fragment key={className}>
                          <div
                            className={`${styles.diffClassItem} ${styles.removed} ${isExpanded ? styles.expanded : ''}`}
                            onClick={(e) => {
                              // Check if clicking the "View Full Documentation" button
                              if ((e.target as HTMLElement).closest('button')) {
                                return; // Let the button handle its own click
                              }
                              if (isExpanded) {
                                setExpandedRemovedClass(null);
                              } else {
                                setExpandedRemovedClass(className);
                              }
                            }}
                          >
                            {className} {isExpanded ? '▼' : '▶'}
                          </div>

                          {isExpanded && classData && (
                            <div className={styles.detailedDiffInline}>
                              <div className={styles.classQuickView}>
                                <h3 className={styles.detailedDiffTitle}>
                                  {className}
                                  <button
                                    className={styles.viewFullButton}
                                    onClick={() => {
                                      // For removed classes, we need to switch versions
                                      // to view the class where it exists (compare version)
                                      setClassHistory([]);
                                      setExpandedRemovedClass(null);

                                      // Swap versions so the removed class becomes viewable
                                      const tempVersion = selectedVersion;
                                      const tempBranch = selectedBranch;
                                      setSelectedVersion(compareVersion);
                                      setSelectedBranch(compareBranch);
                                      setCompareVersion(tempVersion);
                                      setCompareBranch(tempBranch);

                                      // Navigate to class view while maintaining comparison state
                                      setSelectedClass(className);
                                    }}
                                  >
                                    View Full Documentation →
                                  </button>
                                </h3>

                                {classData.d && (
                                  <p className={styles.classDescription}>{classData.d}</p>
                                )}

                                <div className={styles.classMetadata}>
                                  {classData.e && <div><strong>Extends:</strong> {classData.e}</div>}
                                  <div><strong>Category:</strong> {classData.c}</div>
                                  <div><strong>Properties:</strong> {classData.p.length}</div>
                                  <div><strong>Methods:</strong> {classData.m.length}</div>
                                </div>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </section>
              )}

              {versionDiff.modified.length > 0 && (
                <section id="modified-section" className={`${styles.diffSection} ${styles.modified}`}>
                  <h3>Modified Classes ({versionDiff.modified.length})</h3>
                  <div className={styles.modifiedClassesContainer}>
                    {versionDiff.modified.map(className => {
                      const isExpanded = detailedDiff?.className === className;
                      const oldClass = compareData.classes[className];
                      const newClass = data.classes[className];

                      return (
                        <React.Fragment key={className}>
                          <div
                            className={`${styles.diffClassItem} ${styles.modified} ${isExpanded ? styles.expanded : ''}`}
                            onClick={() => {
                              if (isExpanded) {
                                setDetailedDiff(null);
                              } else {
                                const diff = calculateDetailedDiff(className, oldClass, newClass);
                                setDetailedDiff(diff);
                              }
                            }}
                          >
                            {className} {isExpanded ? '▼' : '▶'}
                          </div>

                          {isExpanded && detailedDiff && (
                            <div ref={detailedDiffRef} className={styles.detailedDiffInline}>
                              <h3 className={styles.detailedDiffTitle}>
                                Detailed Changes: <span className={styles.className}>{detailedDiff.className}</span>
                                <button
                                  className={styles.viewFullButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setClassHistory([]);
                                    setSelectedClass(className);
                                  }}
                                >
                                  View Full Documentation →
                                </button>
                              </h3>

                              {/* Properties Comparison */}
                              {(detailedDiff.addedProperties.length > 0 ||
                                detailedDiff.removedProperties.length > 0 ||
                                detailedDiff.modifiedProperties.length > 0) && (
                                <div className={styles.diffSubsection}>
                                  {detailedDiff.addedProperties.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.addedHeading}>Added Properties ({detailedDiff.addedProperties.length})</h5>
                                      <table className={styles.diffTable}>
                                        <thead>
                                          <tr>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Static</th>
                                            <th>Description</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {detailedDiff.addedProperties.map(prop => (
                                            <tr key={prop.n} className={styles.addedRow}>
                                              <td><code>{prop.n}</code></td>
                                              <td><code>{prop.t}</code></td>
                                              <td>{prop.s ? 'Yes' : 'No'}</td>
                                              <td>{prop.d || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {detailedDiff.removedProperties.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.removedHeading}>Removed Properties ({detailedDiff.removedProperties.length})</h5>
                                      <table className={styles.diffTable}>
                                        <thead>
                                          <tr>
                                            <th>Name</th>
                                            <th>Type</th>
                                            <th>Static</th>
                                            <th>Description</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {detailedDiff.removedProperties.map(prop => (
                                            <tr key={prop.n} className={styles.removedRow}>
                                              <td><code>{prop.n}</code></td>
                                              <td><code>{prop.t}</code></td>
                                              <td>{prop.s ? 'Yes' : 'No'}</td>
                                              <td>{prop.d || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}

                                  {detailedDiff.modifiedProperties.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.modifiedHeading}>Modified Properties ({detailedDiff.modifiedProperties.length})</h5>
                                      {detailedDiff.modifiedProperties.map(({ old: oldProp, new: newProp }) => (
                                        <div key={oldProp.n} className={styles.sideBySide}>
                                          <div className={styles.oldVersion}>
                                            <strong>Old:</strong>
                                            <table className={styles.diffTable}>
                                              <tbody>
                                                <tr>
                                                  <td><code>{oldProp.n}</code></td>
                                                  <td><code>{oldProp.t}</code></td>
                                                  <td>{oldProp.s ? 'Yes' : 'No'}</td>
                                                  <td>{oldProp.d || '-'}</td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                          <div className={styles.newVersion}>
                                            <strong>New:</strong>
                                            <table className={styles.diffTable}>
                                              <tbody>
                                                <tr>
                                                  <td><code>{newProp.n}</code></td>
                                                  <td><code>{newProp.t}</code></td>
                                                  <td>{newProp.s ? 'Yes' : 'No'}</td>
                                                  <td>{newProp.d || '-'}</td>
                                                </tr>
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Methods Comparison */}
                              {(detailedDiff.addedMethods.length > 0 ||
                                detailedDiff.removedMethods.length > 0 ||
                                detailedDiff.modifiedMethods.length > 0) && (
                                <div className={styles.diffSubsection}>
                                  {detailedDiff.addedMethods.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.addedHeading}>Added Methods ({detailedDiff.addedMethods.length})</h5>
                                      {detailedDiff.addedMethods.map(method => (
                                        <div key={method.n} className={styles.methodCard}>
                                          <div className={styles.methodHeader}>
                                            <code>{method.s}</code>
                                            {method.st && <span className={styles.badge}>static</span>}
                                            {method.o && <span className={styles.badge}>override</span>}
                                          </div>
                                          {method.d && <p className={styles.methodDesc}>{method.d}</p>}
                                          {method.params && method.params.length > 0 && (
                                            <div className={styles.methodParams}>
                                              <strong>Parameters:</strong>
                                              <ul>
                                                {method.params.map((p, i) => (
                                                  <li key={i}>
                                                    <code>{p.n}: {p.t}</code>
                                                    {p.d && ` - ${p.d}`}
                                                    {p.dv && ` (default: ${p.dv})`}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {method.r && (
                                            <div className={styles.methodReturn}>
                                              <strong>Returns:</strong> {method.r}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {detailedDiff.removedMethods.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.removedHeading}>Removed Methods ({detailedDiff.removedMethods.length})</h5>
                                      {detailedDiff.removedMethods.map(method => (
                                        <div key={method.n} className={styles.methodCard}>
                                          <div className={styles.methodHeader}>
                                            <code>{method.s}</code>
                                            {method.st && <span className={styles.badge}>static</span>}
                                            {method.o && <span className={styles.badge}>override</span>}
                                          </div>
                                          {method.d && <p className={styles.methodDesc}>{method.d}</p>}
                                          {method.params && method.params.length > 0 && (
                                            <div className={styles.methodParams}>
                                              <strong>Parameters:</strong>
                                              <ul>
                                                {method.params.map((p, i) => (
                                                  <li key={i}>
                                                    <code>{p.n}: {p.t}</code>
                                                    {p.d && ` - ${p.d}`}
                                                    {p.dv && ` (default: ${p.dv})`}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {method.r && (
                                            <div className={styles.methodReturn}>
                                              <strong>Returns:</strong> {method.r}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {detailedDiff.modifiedMethods.length > 0 && (
                                    <div className={styles.diffGroup}>
                                      <h5 className={styles.modifiedHeading}>Modified Methods ({detailedDiff.modifiedMethods.length})</h5>
                                      {detailedDiff.modifiedMethods.map(({ old: oldMethod, new: newMethod }) => (
                                        <div key={oldMethod.n} className={styles.sideBySide}>
                                          <div className={styles.oldVersion}>
                                            <strong>Old Version:</strong>
                                            <div className={styles.methodCard}>
                                              <div className={styles.methodHeader}>
                                                <code>{oldMethod.s}</code>
                                                {oldMethod.st && <span className={styles.badge}>static</span>}
                                                {oldMethod.o && <span className={styles.badge}>override</span>}
                                              </div>
                                              {oldMethod.d && <p className={styles.methodDesc}>{oldMethod.d}</p>}
                                              {oldMethod.params && oldMethod.params.length > 0 && (
                                                <div className={styles.methodParams}>
                                                  <strong>Parameters:</strong>
                                                  <ul>
                                                    {oldMethod.params.map((p, i) => (
                                                      <li key={i}>
                                                        <code>{p.n}: {p.t}</code>
                                                        {p.d && ` - ${p.d}`}
                                                        {p.dv && ` (default: ${p.dv})`}
                                                      </li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                              {oldMethod.r && (
                                                <div className={styles.methodReturn}>
                                                  <strong>Returns:</strong> {oldMethod.r}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className={styles.newVersion}>
                                            <strong>New Version:</strong>
                                            <div className={styles.methodCard}>
                                              <div className={styles.methodHeader}>
                                                <code>{newMethod.s}</code>
                                                {newMethod.st && <span className={styles.badge}>static</span>}
                                                {newMethod.o && <span className={styles.badge}>override</span>}
                                              </div>
                                              {newMethod.d && <p className={styles.methodDesc}>{newMethod.d}</p>}
                                              {newMethod.params && newMethod.params.length > 0 && (
                                                <div className={styles.methodParams}>
                                                  <strong>Parameters:</strong>
                                                  <ul>
                                                    {newMethod.params.map((p, i) => (
                                                      <li key={i}>
                                                        <code>{p.n}: {p.t}</code>
                                                        {p.d && ` - ${p.d}`}
                                                        {p.dv && ` (default: ${p.dv})`}
                                                      </li>
                                                    ))}
                                                  </ul>
                                                </div>
                                              )}
                                              {newMethod.r && (
                                                <div className={styles.methodReturn}>
                                                  <strong>Returns:</strong> {newMethod.r}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>
          ) : compareMode && !compareData ? (
            <div className={styles.emptyState}>
              <h2>Loading Comparison Data...</h2>
              <p>Please wait while version data is being loaded.</p>
            </div>
          ) : !currentClass ? (
            <div className={styles.emptyState}>
              <h2>Welcome to DayZ Script Documentation</h2>
              <p>Search for a class or select one from the list to view its documentation.</p>

              {recentlyViewed.length > 0 && (
                <>
                  <h3>Recently Viewed</h3>
                  <div className={styles.popularGrid}>
                    {recentlyViewed.filter(cn => data.classes[cn]).map(className => (
                      <button
                        key={className}
                        className={styles.popularButton}
                        onClick={() => navigateToClass(className)}
                      >
                        {className}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h3>Popular Classes</h3>
              <p className={styles.helpText}>
                Classes ranked by inheritance tree size and method count
              </p>
              <div className={styles.popularGrid}>
                {data.popular.slice(0, 12).map(className => (
                  <button
                    key={className}
                    className={styles.popularButton}
                    onClick={() => navigateToClass(className)}
                  >
                    {className}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.classDetail}>
              <header className={styles.classHeader}>
                {/* Action Button Bar */}
                <div className={styles.actionButtonBar}>
                  <button
                    className={styles.actionButton}
                    onClick={goBack}
                    title="Back"
                  >
                    <span className={styles.buttonIcon}>←</span>
                    <span className={styles.buttonText}>Back</span>
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => {
                      setSelectedClass('');
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    title="Home"
                  >
                    <span className={styles.buttonIcon}>🏠</span>
                    <span className={styles.buttonText}>Home</span>
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => {
                      const url = `${window.location.origin}${window.location.pathname}?class=${encodeURIComponent(currentClass.n)}`;
                      navigator.clipboard.writeText(url).then(() => {
                        // Show temporary success message
                        const btn = document.activeElement as HTMLButtonElement;
                        const iconElement = btn.querySelector(`.${styles.buttonIcon}`);
                        if (iconElement) {
                          const originalIcon = iconElement.innerHTML;
                          iconElement.innerHTML = '✓';
                          setTimeout(() => {
                            iconElement.innerHTML = originalIcon;
                          }, 2000);
                        }
                      }).catch(err => {
                        logger.error('Failed to copy link:', err);
                        alert('Failed to copy link to clipboard');
                      });
                    }}
                    title="Copy direct link to this class"
                  >
                    <span className={styles.buttonIcon}>🔗</span>
                    <span className={styles.buttonText}>Share</span>
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => setShowReportIssueModal(true)}
                    title="Report Issue"
                  >
                    <span className={styles.buttonIcon}>🐛</span>
                    <span className={styles.buttonText}>Report Issue</span>
                  </button>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0 }}>
                    {currentClass.n}
                    <CommunityNoteIcon className={currentClass.n} />
                  </h1>
                  {lastModifiedInfo && (
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      padding: '0.25rem 0.75rem',
                      background: 'var(--ifm-color-primary)',
                      color: 'white',
                      borderRadius: '12px',
                      whiteSpace: 'nowrap'
                    }}
                    title={`Last modified in ${selectedBranch === 'stable' ? 'Stable' : 'Experimental'} ${lastModifiedInfo.build}`}
                    >
                      Modified in {lastModifiedInfo.build}
                    </span>
                  )}
                </div>
                {currentClass.d && <p className={styles.description}>{currentClass.d}</p>}
              </header>

              {/* Inline Comparison Banner - Compact */}
              {compareMode && compareData && inlineClassDiff && inlineClassDiff.status === 'modified' && (
                <div style={{
                  background: 'var(--ifm-color-emphasis-100)',
                  borderRadius: '6px',
                  marginBottom: '1.5rem',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    fontSize: '0.875rem',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {inlineClassDiff.properties.added.length > 0 && (
                      <span style={{ color: '#28a745', fontWeight: '500' }}>
                        +{inlineClassDiff.properties.added.length} prop{inlineClassDiff.properties.added.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {inlineClassDiff.properties.removed.length > 0 && (
                      <span style={{ color: '#dc3545', fontWeight: '500' }}>
                        -{inlineClassDiff.properties.removed.length} prop{inlineClassDiff.properties.removed.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {inlineClassDiff.properties.modified.length > 0 && (
                      <span style={{ color: '#ffc107', fontWeight: '500' }}>
                        ~{inlineClassDiff.properties.modified.length} prop{inlineClassDiff.properties.modified.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {inlineClassDiff.methods.added.length > 0 && (
                      <span style={{ color: '#28a745', fontWeight: '500' }}>
                        +{inlineClassDiff.methods.added.length} method{inlineClassDiff.methods.added.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {inlineClassDiff.methods.removed.length > 0 && (
                      <span style={{ color: '#dc3545', fontWeight: '500' }}>
                        -{inlineClassDiff.methods.removed.length} method{inlineClassDiff.methods.removed.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    {inlineClassDiff.methods.modified.length > 0 && (
                      <span style={{ color: '#ffc107', fontWeight: '500' }}>
                        ~{inlineClassDiff.methods.modified.length} method{inlineClassDiff.methods.modified.length !== 1 ? 's' : ''}
                      </span>
                    )}
                    </div>
                    <button
                      onClick={() => setSelectedClass(null)}
                      style={{
                        padding: '0.5rem 1rem',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--ifm-color-primary)',
                        background: 'transparent',
                        border: '1px solid var(--ifm-color-primary)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--ifm-color-primary)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'var(--ifm-color-primary)';
                      }}
                    >
                      View Full Comparison
                    </button>
                  </div>

                  {/* Expandable Details */}
                  {(inlineClassDiff.properties.modified.length > 0 || inlineClassDiff.methods.modified.length > 0) && (
                    <details style={{ borderTop: '1px solid var(--ifm-color-emphasis-300)' }}>
                      <summary style={{
                        padding: '0.5rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.8125rem',
                        fontWeight: '500',
                        color: 'var(--ifm-color-emphasis-700)',
                        userSelect: 'none'
                      }}>
                        View Modified Details
                      </summary>
                      <div style={{ padding: '0.75rem', paddingTop: 0, fontSize: '0.8125rem' }}>
                        {inlineClassDiff.properties.modified.length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h4 style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#ffc107' }}>Modified Properties</h4>
                            {inlineClassDiff.properties.modified.map((mod: any, i: number) => {
                              // Detect what actually changed in properties
                              const changes: string[] = [];
                              if (mod.old.t !== mod.new.t) changes.push('Type');
                              if (mod.old.s !== mod.new.s) changes.push('Static');

                              return (
                                <div key={i} style={{ marginBottom: '0.75rem', paddingLeft: '0.5rem', borderLeft: '2px solid #ffc107' }}>
                                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{mod.new.n}</div>
                                  <div style={{ fontSize: '0.7rem', marginBottom: '0.25rem', color: '#ffc107', fontStyle: 'italic' }}>
                                    Changed: {changes.join(', ')}
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <div style={{ color: '#dc3545' }}>Before:</div>
                                    <div>Type: <code>{mod.old.t}</code> | Static: {mod.old.s ? 'Yes' : 'No'}</div>
                                    <div style={{ color: '#28a745' }}>After:</div>
                                    <div>Type: <code>{mod.new.t}</code> | Static: {mod.new.s ? 'Yes' : 'No'}</div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {inlineClassDiff.methods.modified.length > 0 && (
                          <div>
                            <h4 style={{ fontSize: '0.875rem', marginBottom: '0.5rem', color: '#ffc107' }}>Modified Methods</h4>
                            {inlineClassDiff.methods.modified.map((mod: any, i: number) => {
                              // Detect what actually changed
                              const changes: string[] = [];
                              if (mod.old.s !== mod.new.s) changes.push('Summary');
                              if (mod.old.d !== mod.new.d) changes.push('Description');
                              if (mod.old.r !== mod.new.r) changes.push('Return type');
                              if (JSON.stringify(mod.old.pr || mod.old.params) !== JSON.stringify(mod.new.pr || mod.new.params)) changes.push('Parameters');
                              if (mod.old.st !== mod.new.st) changes.push('Static');
                              if (mod.old.o !== mod.new.o) changes.push('Override');

                              return (
                                <div key={i} style={{ marginBottom: '0.75rem', paddingLeft: '0.5rem', borderLeft: '2px solid #ffc107' }}>
                                  <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{mod.new.n}</div>
                                  <div style={{ fontSize: '0.7rem', marginBottom: '0.25rem', color: '#ffc107', fontStyle: 'italic' }}>
                                    Changed: {changes.join(', ')}
                                  </div>
                                  <div style={{ fontSize: '0.75rem' }}>
                                    <div style={{ color: '#dc3545', marginBottom: '0.25rem', fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                      <strong>Before:</strong><br />
                                      {mod.old.s && <div>Summary: {mod.old.s}</div>}
                                      {mod.old.d && <div>Description: {mod.old.d}</div>}
                                      Return: <code>{mod.old.r || 'void'}</code> |
                                      Params: {(mod.old.pr || mod.old.params)?.length || 0}
                                      {(mod.old.pr || mod.old.params)?.length > 0 && ` (${(mod.old.pr || mod.old.params).map((p: any) => `${p.t} ${p.n}`).join(', ')})`} |
                                      Static: {mod.old.st ? 'Yes' : 'No'} |
                                      Override: {mod.old.o ? 'Yes' : 'No'}
                                    </div>
                                    <div style={{ color: '#28a745', fontFamily: 'monospace', fontSize: '0.7rem', whiteSpace: 'pre-wrap' }}>
                                      <strong>After:</strong><br />
                                      {mod.new.s && <div>Summary: {mod.new.s}</div>}
                                      {mod.new.d && <div>Description: {mod.new.d}</div>}
                                      Return: <code>{mod.new.r || 'void'}</code> |
                                      Params: {(mod.new.pr || mod.new.params)?.length || 0}
                                      {(mod.new.pr || mod.new.params)?.length > 0 && ` (${(mod.new.pr || mod.new.params).map((p: any) => `${p.t} ${p.n}`).join(', ')})`} |
                                      Static: {mod.new.st ? 'Yes' : 'No'} |
                                      Override: {mod.new.o ? 'Yes' : 'No'}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>
              )}

              <section id="overview" className={styles.overview}>
                <h2>Overview</h2>
                <table className={styles.infoTable}>
                  <tbody>
                    <tr>
                      <th>File</th>
                      <td>
                        <button
                          className={styles.linkButton}
                          onClick={() => {
                            setScriptViewerFile(currentClass.f);
                            setScriptViewerLine(currentClass.l);
                            setShowScriptViewer(true);
                          }}
                          style={{
                            fontFamily: 'monospace',
                            fontSize: '13px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          title="Click to view script file"
                        >
                          <span>📄</span>
                          <code>{currentClass.f}</code>
                        </button>
                      </td>
                    </tr>
                    <tr>
                      <th>Line</th>
                      <td>{currentClass.l}</td>
                    </tr>
                    {currentClass.e && (
                      <tr>
                        <th>Extends</th>
                        <td>
                          <button
                            className={styles.linkButton}
                            onClick={() => navigateToClass(currentClass.e!)}
                          >
                            {currentClass.e}
                          </button>
                        </td>
                      </tr>
                    )}
                    {currentClass.a.length > 0 && (
                      <tr>
                        <th>Inheritance Chain</th>
                        <td>
                          {currentClass.n} → {currentClass.a.map((ancestor, i) => (
                            <React.Fragment key={ancestor}>
                              <button
                                className={styles.linkButton}
                                onClick={() => navigateToClass(ancestor)}
                              >
                                {ancestor}
                              </button>
                              {i < currentClass.a.length - 1 && ' → '}
                            </React.Fragment>
                          ))}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <th>Category</th>
                      <td>{currentClass.c}</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* Inheritance Visualization Section */}
              <section id="inheritance-visualization" className={styles.section}>
                <h2>Inheritance Visualization</h2>
                <div className={styles.visualizationControls}>
                  <button
                    className={`${styles.vizButton} ${visualizationType === 'tree' ? styles.vizButtonActive : ''}`}
                    onClick={() => setVisualizationType(visualizationType === 'tree' ? null : 'tree')}
                  >
                    Tree View
                  </button>
                  <button
                    className={`${styles.vizButton} ${visualizationType === 'force' ? styles.vizButtonActive : ''}`}
                    onClick={() => setVisualizationType(visualizationType === 'force' ? null : 'force')}
                  >
                    Force Graph
                  </button>
                  <button
                    className={`${styles.vizButton} ${visualizationType === 'sunburst' ? styles.vizButtonActive : ''}`}
                    onClick={() => setVisualizationType(visualizationType === 'sunburst' ? null : 'sunburst')}
                  >
                    Sunburst
                  </button>
                </div>

                {visualizationType === 'tree' && (
                  <div className={styles.visualizationContainer}>
                    <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading visualization...</div>}>
                      <InheritanceTree
                        className={currentClass.n}
                        classData={data?.classes}
                        onClassClick={navigateToClass}
                      />
                    </Suspense>
                  </div>
                )}

                {visualizationType === 'force' && (
                  <div className={styles.visualizationContainer}>
                    <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading visualization...</div>}>
                      <ForceGraph
                        className={currentClass.n}
                        classData={data?.classes}
                        maxDepth={2}
                        onClassClick={navigateToClass}
                      />
                    </Suspense>
                  </div>
                )}

                {visualizationType === 'sunburst' && (
                  <div className={styles.visualizationContainer}>
                    <Suspense fallback={<div style={{ padding: '20px', textAlign: 'center' }}>Loading visualization...</div>}>
                      <SunburstDiagram
                        className={currentClass.n}
                        classData={data?.classes}
                        onClassClick={navigateToClass}
                      />
                    </Suspense>
                  </div>
                )}

                {!visualizationType && (
                  <div className={styles.visualizationPlaceholder}>
                    Select a visualization type above to explore the inheritance hierarchy
                  </div>
                )}
              </section>

              {currentClass.cfg && Object.keys(currentClass.cfg).length > 0 && (
                <section id="config-properties" className={styles.section}>
                  <h2>Config Properties</h2>
                  <p style={{ marginBottom: '1rem', color: 'var(--ifm-color-emphasis-700)' }}>
                    Properties loaded from game configuration files (config.cpp) using ConfigGet* methods.
                  </p>
                  {Object.entries(currentClass.cfg).map(([configType, properties]) => (
                    <div key={configType} style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ fontSize: '1.1rem', color: 'var(--ifm-color-primary)' }}>
                        {configType.replace('ConfigGet', '')}
                        <span style={{ fontSize: '0.9rem', color: 'var(--ifm-color-emphasis-600)', marginLeft: '0.5rem' }}>
                          ({properties.length} {properties.length === 1 ? 'property' : 'properties'})
                        </span>
                      </h3>
                      <ul style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '0.5rem',
                        listStyle: 'none',
                        padding: 0
                      }}>
                        {properties.map((prop, idx) => (
                          <li key={idx} style={{
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'var(--ifm-color-emphasis-100)',
                            borderLeft: '3px solid var(--ifm-color-primary)',
                            borderRadius: '4px',
                            fontFamily: 'var(--ifm-font-family-monospace)',
                            fontSize: '0.9rem'
                          }}>
                            {prop}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </section>
              )}

              {/* Class Statistics */}
              {statistics && statistics.classes[currentClass.n] && (
                <section id="class-statistics" className={styles.section}>
                  <h2>Class Statistics</h2>
                  {(() => {
                    const classStats = statistics.classes[currentClass.n];
                    return (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '1rem',
                        marginTop: '1rem'
                      }}>
                        {/* Overview Card */}
                        <div style={{
                          padding: '1rem',
                          background: 'var(--ifm-color-emphasis-100)',
                          borderRadius: '8px',
                          borderLeft: '4px solid var(--ifm-color-primary)'
                        }}>
                          <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: '0.75rem', color: 'var(--ifm-color-primary)' }}>
                            Overview
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Methods:</span>
                              <strong>{classStats.total_methods}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Properties:</span>
                              <strong>{classStats.total_properties}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Inheritance Depth:</span>
                              <strong>{classStats.inheritance_depth}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Top Category:</span>
                              <strong style={{ textTransform: 'capitalize' }}>{classStats.top_category}</strong>
                            </div>
                          </div>
                        </div>

                        {/* Method Characteristics */}
                        <div style={{
                          padding: '1rem',
                          background: 'var(--ifm-color-emphasis-100)',
                          borderRadius: '8px',
                          borderLeft: '4px solid #28a745'
                        }}>
                          <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: '0.75rem', color: '#28a745' }}>
                            Method Characteristics
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Override Count:</span>
                              <strong>{classStats.override_count}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Override Ratio:</span>
                              <strong>{(classStats.override_ratio * 100).toFixed(0)}%</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Static Methods:</span>
                              <strong>{classStats.static_count}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Overloaded Methods:</span>
                              <strong>{classStats.overloaded_methods}</strong>
                            </div>
                          </div>
                        </div>

                        {/* Complexity Metrics */}
                        <div style={{
                          padding: '1rem',
                          background: 'var(--ifm-color-emphasis-100)',
                          borderRadius: '8px',
                          borderLeft: `4px solid ${classStats.complexity_score >= 7 ? '#dc3545' : classStats.complexity_score >= 4 ? '#ffc107' : '#28a745'}`
                        }}>
                          <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: '0.75rem', color: classStats.complexity_score >= 7 ? '#dc3545' : classStats.complexity_score >= 4 ? '#ffc107' : '#28a745' }}>
                            Complexity
                          </h3>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.9rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>Complexity Score:</span>
                              <strong style={{
                                fontSize: '1.5rem',
                                color: classStats.complexity_score >= 7 ? '#dc3545' : classStats.complexity_score >= 4 ? '#ffc107' : '#28a745'
                              }}>{classStats.complexity_score}/10</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Avg Parameters:</span>
                              <strong>{classStats.avg_parameters}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>Max Parameters:</span>
                              <strong>{classStats.max_parameters}</strong>
                            </div>
                            <div style={{
                              marginTop: '0.5rem',
                              padding: '0.5rem',
                              background: 'var(--ifm-background-color)',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              color: 'var(--ifm-color-emphasis-600)'
                            }}>
                              {classStats.complexity_score < 4 ? 'Simple class, easy to maintain' :
                               classStats.complexity_score < 7 ? 'Moderate complexity' :
                               'High complexity, review for refactoring'}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </section>
              )}

              {currentClass.p.length > 0 && (
                <section id="properties" className={styles.section}>
                  <h2>Properties ({currentClass.p.length})</h2>
                  <table className={styles.membersTable}>
                    <thead>
                      <tr>
                        {inlineClassDiff && inlineClassDiff.status === 'modified' && <th>Status</th>}
                        <th>Name</th>
                        <th>Type</th>
                        <th>Static</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentClass.p.map((prop, i) => {
                        let propStatus = 'unchanged';
                        if (inlineClassDiff && inlineClassDiff.status === 'modified') {
                          if (inlineClassDiff.properties.added.some(p => p.n === prop.n)) {
                            propStatus = 'added';
                          } else if (inlineClassDiff.properties.modified.some(p => p.new.n === prop.n)) {
                            propStatus = 'modified';
                          }
                        }

                        const rowClass = propStatus === 'added' ? styles.addedRow :
                                       propStatus === 'modified' ? styles.removedRow : '';

                        return (
                          <tr key={i} className={rowClass}>
                            {inlineClassDiff && inlineClassDiff.status === 'modified' && (
                              <td>
                                {propStatus === 'added' && <span style={{color: '#28a745', fontWeight: 'bold'}}>+ Added</span>}
                                {propStatus === 'modified' && <span style={{color: '#ffc107', fontWeight: 'bold'}}>~ Modified</span>}
                                {propStatus === 'unchanged' && <span style={{color: '#6c757d'}}>-</span>}
                              </td>
                            )}
                            <td>
                              <code>{prop.n}</code>
                              <CommunityNoteIcon className={currentClass.n} propertyName={prop.n} />
                            </td>
                            <td><code>{prop.t}</code></td>
                            <td>{prop.s ? '✓' : '-'}</td>
                            <td>{prop.d || '-'}</td>
                          </tr>
                        );
                      })}
                      {/* Show removed properties */}
                      {inlineClassDiff && inlineClassDiff.status === 'modified' && inlineClassDiff.properties.removed.map((prop, i) => (
                        <tr key={`removed-${i}`} className={styles.removedRow}>
                          <td><span style={{color: '#dc3545', fontWeight: 'bold'}}>- Removed</span></td>
                          <td><code>{prop.n}</code></td>
                          <td><code>{prop.t}</code></td>
                          <td>{prop.s ? '✓' : '-'}</td>
                          <td>{prop.d || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {currentClass.m.length > 0 && (
                <section id="methods" className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <h2 style={{ margin: 0 }}>Methods ({currentClass.m.filter(method =>
                      methodCategoryFilter === 'all' || (method.cat && method.cat.includes(methodCategoryFilter))
                    ).length} / {currentClass.m.length})</h2>
                    <select
                      value={methodCategoryFilter}
                      onChange={(e) => setMethodCategoryFilter(e.target.value)}
                      style={{
                        padding: '0.5rem',
                        fontSize: '0.875rem',
                        borderRadius: '4px',
                        border: '1px solid var(--ifm-color-emphasis-300)',
                        background: 'var(--ifm-background-color)',
                        color: 'var(--ifm-font-color-base)'
                      }}
                    >
                      <option value="all">All Categories</option>
                      {/* Core Categories */}
                      <option value="accessors">Accessors (24.2%)</option>
                      <option value="events">Events & Callbacks (11.5%)</option>
                      <option value="lifecycle">Lifecycle (8.6%)</option>
                      <option value="inventory">Inventory (5.0%)</option>
                      <option value="actions">Actions (3.9%)</option>
                      <option value="syntax">Syntax (3.6%)</option>
                      <option value="static">Static (3.1%)</option>

                      {/* UI & Display */}
                      <option value="uidisplay">UI & Display (3.0%)</option>

                      {/* Visual & Audio */}
                      <option value="visualeffects">Visual Effects (2.8%)</option>
                      <option value="audio">Audio (1.9%)</option>

                      {/* Validation & State */}
                      <option value="validation">Validation (2.5%)</option>
                      <option value="state">State Management (1.2%)</option>

                      {/* Utility & Combat */}
                      <option value="utility">Utility (2.2%)</option>
                      <option value="combat">Combat (2.1%)</option>

                      {/* Core Systems */}
                      <option value="debug">Debug (1.9%)</option>
                      <option value="updates">Updates (1.9%)</option>
                      <option value="input">Input (1.7%)</option>

                      {/* Selection & Network */}
                      <option value="selection">Selection (1.3%)</option>
                      <option value="network">Network (1.1%)</option>
                      <option value="system">System (1.2%)</option>

                      {/* Layout & Graphics */}
                      <option value="layout">Layout (0.8%)</option>
                      <option value="graphics">Graphics (0.6%)</option>

                      {/* Gameplay & Player */}
                      <option value="gameplay">Gameplay (0.8%)</option>
                      <option value="player">Player Systems (0.8%)</option>

                      {/* Data & IO */}
                      <option value="data">Data (0.8%)</option>
                      <option value="io">I/O (0.6%)</option>

                      {/* Progress & Timing */}
                      <option value="progress">Progress (0.8%)</option>
                      <option value="timing">Timing (0.5%)</option>

                      {/* Specialized */}
                      <option value="indicators">Indicators (1.0%)</option>
                      <option value="component">Components (0.7%)</option>
                      <option value="transform">Transform (0.7%)</option>
                      <option value="control">Control Flow (0.6%)</option>

                      {/* Lower Usage */}
                      <option value="filters">Filters (0.3%)</option>
                      <option value="security">Security (0.4%)</option>
                      <option value="construction">Construction (0.2%)</option>

                      <option value="other">Other (5.7%)</option>
                    </select>
                  </div>
                  {currentClass.m.filter(method =>
                    methodCategoryFilter === 'all' || (method.cat && method.cat.includes(methodCategoryFilter))
                  ).map((method, i) => {
                    let methodStatus = 'unchanged';
                    if (inlineClassDiff && inlineClassDiff.status === 'modified') {
                      if (inlineClassDiff.methods.added.some(m => m.n === method.n)) {
                        methodStatus = 'added';
                      } else if (inlineClassDiff.methods.modified.some(m => m.new.n === method.n)) {
                        methodStatus = 'modified';
                      }
                    }

                    return (
                      <div key={i} className={styles.method} style={{
                        borderLeft: methodStatus === 'added' ? '4px solid #28a745' :
                                   methodStatus === 'modified' ? '4px solid #ffc107' : 'none',
                        backgroundColor: methodStatus === 'added' ? 'rgba(40, 167, 69, 0.05)' :
                                        methodStatus === 'modified' ? 'rgba(255, 193, 7, 0.05)' : 'transparent'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <h3 style={{ margin: 0, flex: 1 }}>
                            {method.n}
                            <CommunityNoteIcon className={currentClass.n} methodName={method.n} />
                          </h3>
                          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                            {method.cat && method.cat.map((category, catIndex) => (
                              <CategoryBadge key={catIndex} category={category} />
                            ))}
                          </div>
                          {inlineClassDiff && inlineClassDiff.status === 'modified' && methodStatus !== 'unchanged' && (
                            <span style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              backgroundColor: methodStatus === 'added' ? '#28a745' :
                                             methodStatus === 'modified' ? '#ffc107' : '#6c757d',
                              color: methodStatus === 'modified' ? '#000' : '#fff'
                            }}>
                              {methodStatus === 'added' ? '+ ADDED' : '~ MODIFIED'}
                            </span>
                          )}
                        </div>
                        <pre className={styles.signature}>{method.s}</pre>

                        {method.d && <p>{method.d}</p>}

                        {method.params.length > 0 && (
                          <div>
                            <h4>Parameters:</h4>
                            <ul>
                              {method.params.map((param, j) => (
                                <li key={j}>
                                  <code>{param.n}</code> (<code>{param.t}</code>)
                                  {param.d && ` - ${param.d}`}
                                  {param.dv && ` (default: ${param.dv})`}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {method.r && (
                          <div>
                            <h4>Returns:</h4>
                            <p>{method.r}</p>
                          </div>
                        )}

                        <div className={styles.methodFlags}>
                          {method.st && <span className={styles.flag}>static</span>}
                          {method.o && <span className={styles.flag}>override</span>}
                        </div>
                      </div>
                    );
                  })}
                  {/* Show removed methods */}
                  {inlineClassDiff && inlineClassDiff.status === 'modified' && inlineClassDiff.methods.removed.map((method, i) => (
                    <div key={`removed-${i}`} className={styles.method} style={{
                      borderLeft: '4px solid #dc3545',
                      backgroundColor: 'rgba(220, 53, 69, 0.05)'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <h3 style={{ margin: 0, flex: 1 }}>{method.n}</h3>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: '#dc3545',
                          color: '#fff'
                        }}>
                          - REMOVED
                        </span>
                      </div>
                      <pre className={styles.signature}>{method.s}</pre>
                      {method.d && <p>{method.d}</p>}
                    </div>
                  ))}
                </section>
              )}

              {/* Table of Contents - Fixed Position */}
              {selectedClass && (
                <TableOfContents sections={tocSections} />
              )}
            </div>
          )}
        </main>
        </div>
      )}


      {/* Report Issue Modal */}
      <ReportIssueModal
        isOpen={showReportIssueModal}
        onClose={() => setShowReportIssueModal(false)}
        className={currentClass?.n || ''}
      />

      {/* Script File Viewer Modal */}
      <ScriptFileViewer
        isOpen={showScriptViewer}
        onClose={() => setShowScriptViewer(false)}
        filePath={scriptViewerFile}
        lineNumber={scriptViewerLine}
        version={buildLabel(selectedBranch, selectedVersion)}
        branch={selectedBranch}
        methodUsageIndex={methodUsageIndex}
        onSearch={(searchTerm) => {
          setSearchTerm(searchTerm);
          setShowScriptViewer(false);
        }}
      />

      {/* Mobile Menu Toggle Button */}
      <button
        className={styles.mobileMenuToggle}
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        aria-label="Toggle menu"
      >
        {mobileSidebarOpen ? '✕' : '☰'}
      </button>

      {/* Floating Search Button for Text Selection */}
      {selectionSearch.isVisible && (
        <FloatingSearchButton
          text={selectionSearch.text}
          usageCount={selectionSearch.usageCount}
          position={selectionSearch.position}
          onSearch={handleSelectionSearch}
          onClose={selectionSearch.handleDismiss}
        />
      )}
    </>
  );
}
